<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ru';
import { useInstitutionStore } from '@entities/institution';
import { useCheckoutFormStore } from '@features/basket';
import { useModalStore } from '@shared/model';
import { useApi } from '@shared/api';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ru');

interface PreorderSlot {
  iso: string;
  end: string;
  remaining: number | null;
  available: boolean;
}
interface PreorderSlotsResponse {
  preorder_enabled: boolean;
  outside_hours_allowed: boolean;
  date: string;
  order_type: 'delivery' | 'pickup';
  timezone: string;
  asap: { min_minutes: number; max_minutes: number; available: boolean };
  slots: PreorderSlot[];
  unavailable_reason?: string;
}

const MAX_DAYS_AHEAD = 30;
const ASAP_VALUE = 'asap';

const storeInstitution = useInstitutionStore();
const checkoutFormStore = useCheckoutFormStore();
const storeModal = useModalStore();

const branchId = computed(() => storeInstitution.selectedBranch?.id ?? null);
const branchTimezone = computed(
  () => storeInstitution.selectedBranch?.working_hours?.timezone || 'Europe/Moscow',
);

// Запрос на бэк отправляем как 'delivery' | 'pickup'.
// 'dine_in' → 'pickup' (без курьера).
const orderType = computed<'delivery' | 'pickup'>(() =>
  checkoutFormStore.getForm.method_receipt === 'delivery' ? 'delivery' : 'pickup',
);

const days = computed(() => {
  const now = dayjs().tz(branchTimezone.value);
  const items: Array<{ iso: string; label: string; sub: string }> = [];
  for (let i = 0; i < MAX_DAYS_AHEAD; i++) {
    const d = now.add(i, 'day').startOf('day');
    if (i === 0) items.push({ iso: d.format('YYYY-MM-DD'), label: 'Сегодня', sub: '' });
    else if (i === 1) items.push({ iso: d.format('YYYY-MM-DD'), label: 'Завтра', sub: '' });
    else items.push({ iso: d.format('YYYY-MM-DD'), label: d.format('dd').toLowerCase(), sub: d.format('D') });
  }
  return items;
});

const selectedDay = ref<string>(days.value[0]?.iso ?? dayjs().format('YYYY-MM-DD'));
const selectedSlot = ref<string>(ASAP_VALUE);
const slotsData = ref<PreorderSlotsResponse | null>(null);
const isLoading = ref(false);
const fetchError = ref<string | null>(null);

// Горизонтальная лента дат: на тач свайп работает нативно, но на ПК колесо мыши
// вертикальное и не прокручивает горизонтальный контейнер — проксируем deltaY → scrollLeft.
const daysRef = ref<HTMLElement | null>(null);
function onDaysWheel(e: WheelEvent) {
  const el = daysRef.value;
  if (!el || el.scrollWidth <= el.clientWidth) return;
  // Не трогаем горизонтальные жесты трекпада (там deltaX уже работает нативно)
  if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
  el.scrollLeft += e.deltaY;
  e.preventDefault();
}

const isTodaySelected = computed(() => selectedDay.value === days.value[0]?.iso);

async function fetchSlots() {
  if (!branchId.value) return;
  isLoading.value = true;
  fetchError.value = null;
  try {
    const { data } = await useApi(true).get<PreorderSlotsResponse>(
      `/institutions/branches/${branchId.value}/preorder-slots`,
      { params: { date: selectedDay.value, type: orderType.value } },
    );
    slotsData.value = data;
  } catch (error: unknown) {
    fetchError.value = (error as Error)?.message ?? 'Не удалось загрузить слоты';
    slotsData.value = null;
  } finally {
    isLoading.value = false;
  }
}

watch([branchId, selectedDay, orderType], fetchSlots, { immediate: false });

const slots = computed<Array<{ iso: string; label: string; remaining: number | null }>>(() => {
  const list = slotsData.value?.slots ?? [];
  return list.map((s) => {
    const start = dayjs(s.iso).tz(branchTimezone.value);
    const end = dayjs(s.end).tz(branchTimezone.value);
    const label = start.isSame(end)
      ? start.format('HH:mm')
      : `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
    return { iso: s.iso, label, remaining: s.remaining };
  });
});

const asapInfo = computed(() => slotsData.value?.asap ?? null);
const isAsapAvailable = computed(() => Boolean(asapInfo.value?.available));
const isPreorderEnabled = computed(() => slotsData.value?.preorder_enabled !== false);

// Метка выбранного времени для отображения в шапке модалки
const summaryLabel = computed(() => {
  if (selectedSlot.value === ASAP_VALUE || !selectedSlot.value) {
    const asap = asapInfo.value;
    if (!asap) return 'Ближайшее';
    const asapMin = asap.min_minutes;
    const asapMax = asap.max_minutes;
    if (asapMin === asapMax) return `Ближайшее · ${asapMin} мин`;
    return `Ближайшее · ${asapMin}–${asapMax} мин`;
  }
  const slot = slots.value.find((s) => s.iso === selectedSlot.value);
  const slotLabel = slot?.label ?? '';
  const dayItem = days.value.find((d) => d.iso === selectedDay.value);
  const dayLabel = dayItem
    ? dayItem.sub ? `${dayItem.label} ${dayItem.sub}` : dayItem.label
    : selectedDay.value;
  return slotLabel ? `${dayLabel} · ${slotLabel}` : dayLabel;
});

// Бейдж в слоте при заданном лимите заказов
function slotBadge(remaining: number | null): { label: string; tone: 'last' | 'few' } | null {
  if (remaining === null) return null;
  if (remaining === 1) return { label: 'Последнее место', tone: 'last' };
  if (remaining > 1 && remaining <= 3) return { label: 'Мало мест', tone: 'few' };
  return null;
}

function onSelectDay(iso: string) {
  selectedDay.value = iso;
  // Логика "по умолчанию": сегодня → ASAP (если доступно), иначе первый слот
  if (isTodaySelected.value && isAsapAvailable.value) {
    selectedSlot.value = ASAP_VALUE;
  } else if (slots.value.length > 0) {
    selectedSlot.value = slots.value[0].iso;
  } else {
    selectedSlot.value = '';
  }
}

// Применяет текущий выбор к стору (без закрытия)
function applySelection() {
  if (selectedSlot.value === ASAP_VALUE || !selectedSlot.value) {
    checkoutFormStore.setField('preorder_mode', 'asap');
    checkoutFormStore.setField('reservation_time', null);
  } else {
    checkoutFormStore.setField('preorder_mode', 'scheduled');
    checkoutFormStore.setField('reservation_time', selectedSlot.value);
  }
}

function save() {
  applySelection();
  storeModal.close();
}

// Автосохранение при закрытии через X-кнопку, оверлей или аппаратную кнопку Назад
onBeforeUnmount(() => {
  applySelection();
});

onMounted(async () => {
  await fetchSlots();
  const form = checkoutFormStore.getForm;
  if (form.preorder_mode === 'scheduled' && form.reservation_time) {
    const reservation = dayjs(form.reservation_time).tz(branchTimezone.value);
    const dayIso = reservation.format('YYYY-MM-DD');
    if (days.value.some((d) => d.iso === dayIso)) {
      selectedDay.value = dayIso;
      // После смены selectedDay watcher перезапросит слоты; ждём в следующем тике
      await fetchSlots();
      const matched = slots.value.find((s) => dayjs(s.iso).isSame(reservation, 'minute'));
      selectedSlot.value = matched?.iso ?? form.reservation_time;
    }
  } else {
    selectedDay.value = days.value[0]?.iso ?? selectedDay.value;
    selectedSlot.value = ASAP_VALUE;
  }
});
</script>

<template>
  <div class="preorder-modal">
    <h3 class="preorder-modal__title">Выберите день и время</h3>

    <div ref="daysRef" class="preorder-modal__days" @wheel="onDaysWheel">
      <button
        v-for="day in days"
        :key="day.iso"
        type="button"
        :class="['day-chip', { 'day-chip--active': selectedDay === day.iso }]"
        @click="onSelectDay(day.iso)"
      >
        <span class="day-chip__label">{{ day.label }}</span>
        <span v-if="day.sub" class="day-chip__sub">{{ day.sub }}</span>
      </button>
    </div>

    <div class="preorder-modal__slots">
      <div v-if="!isPreorderEnabled" class="preorder-modal__empty">
        Предзаказ на этом филиале временно недоступен
      </div>
      <div v-else-if="isLoading" class="preorder-modal__empty">Загрузка слотов…</div>
      <div v-else-if="fetchError" class="preorder-modal__empty">
        Не удалось загрузить слоты. Попробуйте ещё раз
      </div>
      <template v-else>
        <UiKitRadioGroup v-model="selectedSlot" color="default" class="preorder-modal__radios">
          <UiKitRadio
            v-if="isTodaySelected && isAsapAvailable"
            :value="ASAP_VALUE"
            class="slot-item"
          >
            <span class="slot-item__label slot-item__label--asap">{{ summaryLabel }}</span>
          </UiKitRadio>

          <UiKitRadio v-for="slot in slots" :key="slot.iso" :value="slot.iso" class="slot-item">
            <span class="slot-item__row">
              <span class="slot-item__label">{{ slot.label }}</span>
              <span
                v-if="slotBadge(slot.remaining)"
                class="slot-item__badge"
                :class="{ 'slot-item__badge--last': slotBadge(slot.remaining)?.tone === 'last' }"
              >
                {{ slotBadge(slot.remaining)?.label }}
              </span>
            </span>
          </UiKitRadio>
        </UiKitRadioGroup>

        <div v-if="slots.length === 0 && !(isTodaySelected && isAsapAvailable)" class="preorder-modal__empty">
          В выбранный день нет доступных слотов
        </div>
      </template>
    </div>

    <div class="preorder-modal__footer">
      <UiButtonMain
        size="xl"
        block
        color="red"
        rounded="rounded-3xl"
        label="Готово"
        :disabled="!selectedSlot || !isPreorderEnabled"
        @click="save"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.preorder-modal {
  // overflow-hidden: модалка не скроллится целиком — скроллятся только слоты внутри
  // (иначе на промежуточных ширинах контент «проскальзывал» поверх корзины).
  @apply flex flex-col px-4 overflow-hidden;
  @apply max-lg:h-[calc(100dvh-var(--safe-area-inset-top))];   // mobile: заполняет fullscreen-модал
  @apply lg:max-h-[662px];                                     // desktop: капирует высоту, слоты скроллятся внутри

  &__title {
    @apply pt-6 text-xl font-bold text-hui-foreground;
  }

  &__days {
    @apply flex gap-2 overflow-x-auto pb-4 flex-shrink-0;
    padding-top: 3rem;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__slots {
    @apply flex-1 min-h-0 overflow-y-auto;
  }

  &__empty {
    @apply text-hui-foreground/50 text-center py-8;
  }

  &__footer {
    @apply flex-shrink-0 py-4;
    @apply border-t border-hui-divider;
  }

  // Радио-группа слотов (UiKitRadioGroup): строки вплотную, с разделителями
  &__radios {
    :deep(div) {
      @apply gap-0;
    }

    :deep(.slot-item) {
      @apply w-full py-3 px-2 rounded-lg transition-colors duration-150;
      @apply border-b border-hui-divider;

      &:hover {
        @apply bg-hui-content2;
      }

      // текстовая обёртка UiKitRadio занимает всю ширину строки (для justify-between бейджа)
      > span:last-child {
        @apply flex-1 min-w-0;
      }
    }
  }
}

.day-chip {
  @apply flex flex-col items-center justify-center flex-shrink-0;
  @apply min-w-[64px] px-4 py-2 rounded-hui-lg;
  @apply bg-hui-content2 text-hui-foreground/70 border border-hui-divider;
  @apply transition-all duration-200;

  &:hover {
    @apply bg-hui-content3/60;
  }

  &__label {
    @apply text-sm font-medium leading-tight;
  }

  &__sub {
    @apply text-xs text-hui-foreground/50 mt-0.5;
  }

  &--active {
    @apply bg-hui-content3 text-hui-foreground border-hui-foreground/20 shadow-sm;

    .day-chip__sub {
      @apply text-hui-foreground/70;
    }
  }
}

// Содержимое слота (slot-content рендерится в скоупе родителя — обычные scoped-стили)
.slot-item__row {
  @apply flex items-center justify-between gap-2 w-full;
}

.slot-item__label {
  @apply text-base text-hui-foreground;

  &--asap {
    @apply font-medium;
  }
}

.slot-item__badge {
  @apply text-xs font-medium px-2 py-1 rounded-full shrink-0;
  @apply bg-hui-default-100 text-hui-foreground/70;

  &--last {
    @apply bg-hui-danger/10 text-hui-danger;
  }
}
</style>
